// Financing (Acorn Finance pre-qualification) — the ONE place the financing
// link and the approved financing facts live. Prompt 1 of 3 builds /financing;
// Prompts 2 and 3 reuse these exports on the home page, /pricing, service
// pages, the estimator and the forms. Never hard-code the URL anywhere else.
//
// Rules for every financing CTA (enforced by <FinancingLink>):
//   • the URL byte-for-byte as below (d=FB4O6 credits IDB Plumbing)
//   • target="_blank" rel="noopener noreferrer"
//   • aria-label ending with "(opens in a new tab)"
//   • data-placement="<placement>" + a `financing_click` GTM event
//   • a plain outbound link — no third-party script or widget
//
// Copy rules: no APR / interest-rate figures, no monthly-payment amounts, no
// "as low as", "0%", "no credit check", "guaranteed" or funding timelines, and
// never call IDB Plumbing or Acorn Finance a lender.

import { PRICING } from "./pricing";

export const FINANCING_URL = "https://www.acornfinance.com/pre-qualify/?d=FB4O6&utm_medium=user_pre_qual_link";

/** Section 9 payment estimator — ships OFF. Flip to true to render it between "What you can finance" and "Straight answers". */
export const SHOW_PAYMENT_ESTIMATOR = false;

export const LOAN_MIN = 1000;
export const LOAN_MAX = 100000;
export const LOAN_DEFAULT = 5000;
export const TERM_YEARS = [2, 3, 5, 7, 10, 12, 15, 20];
export const TERM_DEFAULT = 5;

/** Standard short disclaimer — reuse this exact text wherever a disclaimer is called for. */
export const FINANCING_DISCLAIMER =
  "Financing is offered through Acorn Finance's network of third-party lenders and is subject to credit approval. IDB Plumbing Solutions and Acorn Finance are not lenders. Pre-qualifying uses a soft credit inquiry that does not affect your credit score; a lender will perform a hard inquiry only if you choose to proceed with an offer. Rates, terms, and loan amounts vary by lender and applicant and are subject to change without notice.";

/** Extra sentence that follows the standard disclaimer on /financing (section 8). */
export const FINANCING_DISCLAIMER_EXTRA =
  "Loan amounts, terms, and lender availability are set by Acorn Finance and its lending partners and may change without notice.";

// ---- /financing page content -------------------------------------------------

export const FINANCING_SEO = {
  title: "Plumbing Financing from $1,000 | IDB Plumbing",
  description: "Finance your water heater, repipe, or sewer line. Pre-qualify in minutes with no impact to your credit score. $1,000–$100,000, terms up to 20 years.",
  path: "/financing",
};

export const FINANCING_HERO = {
  eyebrow: "Financing",
  h1: "Plumbing financing that keeps the job moving.",
  sub: "Pre-qualify in a few minutes for $1,000 to $100,000 with no impact to your credit score. Compare fixed-rate offers from multiple lenders, choose the one that fits, and pay the same flat-rate price you already approved.",
  cta: "Check Your Rate — No Impact to Credit Score",
};

/** Four trust points under the hero buttons (icons are Lucide names resolved in the page). */
export const FINANCING_TRUST_POINTS = [
  { icon: "BadgeDollarSign", text: "$1,000 – $100,000" },
  { icon: "CalendarRange", text: "Terms up to 20 years" },
  { icon: "ShieldCheck", text: "Fixed-rate loans" },
  { icon: "Scale", text: "Multiple lenders compete" },
];

export const FINANCING_STEPS = [
  { title: "Answer a few questions", text: "Takes a few minutes online. A soft credit pull shows you what you pre-qualify for without affecting your score." },
  { title: "Compare offers and apply", text: "See offers from Acorn Finance's marketplace of lenders side by side. Pick the one you like and apply directly with that lender." },
  { title: "Get funded, get it fixed", text: "If approved, the funds go to you. You pay IDB Plumbing the flat-rate price you approved, and pay the lender back monthly." },
];

export const FINANCING_STEPS_NOTE = "Start the pre-qualification whenever you like — before your estimate, after it, or while our plumber is on site.";

// -- dollar ranges: pulled from the same PRICING data the /pricing page renders --

const PRICING_BY_SLUG = Object.fromEntries(PRICING.map((p) => [p.slug, p]));
const RANGE_RE = /\$[\d,]+\s?[–-]\s?\$[\d,]+\+?/;
const tidyRange = (s) => s.replace(/\s?[–-]\s?/, " – ");

/**
 * The dollar range /pricing shows for a job. With no `keyword` it is the card's
 * headline range (e.g. water-heaters → "$1,600 – $3,200"); with a keyword it is
 * the first "$X–$Y" figure that follows that word in the card's note (e.g.
 * "repipes" → "$4,000 – $15,000"). Falls back to the headline range, so the
 * financing page can never show a number that is not on /pricing.
 */
export const pricingRange = (slug, keyword) => {
  const card = PRICING_BY_SLUG[slug];
  if (!card) return null;
  if (keyword) {
    const idx = (card.note || "").indexOf(keyword);
    const hit = idx >= 0 ? card.note.slice(idx).match(RANGE_RE) : null;
    if (hit) return tidyRange(hit[0]);
  }
  return card.range;
};

/** Six "What you can finance" cards. `range` strings come from PRICING via pricingRange(). */
export const FINANCING_JOBS = [
  {
    id: "water-heater",
    icon: "Flame",
    title: "Water heater replacement",
    text: "Standard tank, tankless, or heat-pump.",
    href: "/services/water-heaters",
    range: pricingRange("water-heaters"),
    rangeLabel: "standard tank, installed",
    secondary: { range: pricingRange("water-heaters", "Tankless"), label: "tankless or heat-pump" },
  },
  {
    id: "repipe",
    icon: "Wrench",
    title: "Whole-home repipe",
    text: "PEX repipes on older galvanized or polybutylene homes.",
    href: "/services/repiping-pipe-repair",
    range: pricingRange("repiping-pipe-repair", "repipes"),
    rangeLabel: "whole-home PEX, by home size",
  },
  {
    id: "sewer-line",
    icon: "Shovel",
    title: "Sewer line replacement",
    text: "Trenchless or open-trench; often the largest unplanned plumbing cost a homeowner faces.",
    href: "/services/water-sewer-line-replacement",
    range: pricingRange("water-sewer-line-replacement"),
    rangeLabel: "full line replacement",
  },
  {
    id: "water-service-line",
    icon: "Droplets",
    title: "Water service line replacement",
    text: "From the meter to the house.",
    href: "/services/water-sewer-line-replacement",
    range: pricingRange("water-sewer-line-replacement", "Water service lines"),
    rangeLabel: "meter to house",
  },
  {
    id: "fixtures",
    icon: "ShowerHead",
    title: "Bathroom and kitchen fixture upgrades",
    text: "Multiple fixtures, rough-in changes, remodel plumbing.",
    href: "/services/fixture-installation",
    range: pricingRange("fixture-installation"),
    rangeLabel: "per fixture, installed",
  },
  {
    id: "gas-lines",
    icon: "FlameKindling",
    title: "Gas line runs",
    text: "New appliance hookups, range and dryer conversions, outdoor lines.",
    href: "/services/gas-lines",
    range: pricingRange("gas-lines"),
    rangeLabel: "per run / hookup",
  },
];

export const FINANCING_JOBS_NOTE = "Any IDB Plumbing job of $1,000 or more is eligible, including emergency repairs.";

export const FINANCING_POINTS = [
  { icon: "Handshake", title: "Same price, financed or not.", text: "Your flat-rate quote is the same whether you pay by card, check, or financing. We never mark up a job because it's financed." },
  { icon: "DoorOpen", title: "No pressure, no paperwork at the door.", text: "You pre-qualify on your phone or computer, on your own time. Our plumbers don't sell loans." },
  { icon: "Scale", title: "Real lender competition.", text: "Acorn Finance shows you offers from multiple lenders so you're not stuck with one rate." },
  { icon: "SearchCheck", title: "Checking costs nothing and doesn't touch your score.", text: "Pre-qualifying is a soft credit pull. A hard pull only happens if you decide to move forward with a lender's offer." },
];

export const FINANCING_BAND = {
  title: "See what you pre-qualify for in minutes.",
  cta: "Check Your Rate",
  callText: "Or call (425) 243-4006 with questions",
};

/** Financing FAQ — also feeds the FAQPage JSON-LD. Exact spec wording. */
export const FINANCING_FAQS = [
  { q: "Will checking my options affect my credit score?", a: "No. Pre-qualifying uses a soft credit inquiry, which does not affect your score. A lender will run a hard inquiry only if you choose to proceed with that lender's offer." },
  { q: "How much can I finance?", a: "Lenders in Acorn Finance's network offer loans from $1,000 up to $100,000 to qualified consumers, with terms up to 20 years. The amount and terms you're offered depend on the lender and your credit profile." },
  { q: "Who is the lender?", a: "IDB Plumbing Solutions is not a lender and does not make credit decisions. Acorn Finance is also not a lender — it's a marketplace that matches you with third-party lenders. You apply with and repay the lender you choose." },
  { q: "Does financing change my price?", a: "No. You get the same flat-rate quote, approved in writing before work starts, regardless of how you pay." },
  { q: "How does IDB Plumbing get paid?", a: "Once a lender approves and funds your loan, the funds go to you. You pay IDB Plumbing the same way as any other job, and repay the lender monthly." },
  { q: "Can I finance an emergency repair?", a: "Yes, as long as the job is $1,000 or more. You can start the pre-qualification while our plumber is on site." },
  { q: "Can I pay the loan off early?", a: "That depends on the lender. Each offer shows its own terms, including any prepayment rules — read them before you accept." },
  { q: "Are there fees?", a: "Checking your options is free. Some lenders charge an origination fee, which is disclosed in that lender's offer before you accept it. IDB Plumbing charges nothing for financing." },
  { q: "I'm a landlord, builder, or commercial customer — does this apply to me?", a: "Acorn Finance is consumer financing for individuals. For commercial accounts and builders, call (425) 243-4006 to discuss payment terms." },
  { q: "Where do I start?", a: "Click Check Your Rate, answer a few questions, and compare offers. Or call (425) 243-4006 and we'll walk you through it." },
];

export const FINANCING_CLOSING = {
  title: "Ready when you are.",
  sub: "Pre-qualify in a few minutes, or get a flat-rate estimate first. Licensed, bonded, and insured across Washington State.",
  cta: "Check Your Rate",
  secondary: "Get an Instant Estimate",
};

// ---- Prompt 2: sitewide copy (nav, footer, service pages, pricing band, home band, promo line) ----

/** Service-page "Financing available" card (shared by all eight service pages). */
export const SERVICE_FINANCING = {
  heading: "Financing available",
  body: "Larger jobs don't have to wait on a lump sum. Pre-qualify in minutes for $1,000 to $100,000 with no impact to your credit score, compare offers from multiple lenders, and keep the job moving.",
  commercialBody: "Acorn Finance is consumer financing for homeowners. For commercial accounts and builders, contact us about payment terms.",
  cta: "Check Your Rate",
  link: "How financing works",
  note: "Soft credit pull to pre-qualify. Subject to credit approval.",
};

/** /pricing — band between the price table and "What moves the price up or down". */
export const PRICING_FINANCING_BAND = {
  title: "Need to spread out the cost?",
  body: "Water heaters, repipes, and sewer lines are the jobs that financing was built for. Pre-qualify for $1,000 to $100,000 in a few minutes with a soft credit pull, compare offers from multiple lenders, and pick the one that fits.",
  cta: "Check Your Rate",
  link: "How financing works",
};

/** Home — band between "Why IDB" and "Where we work". */
export const HOME_FINANCING_BAND = {
  title: "Financing available on jobs $1,000 and up",
  body: "Pre-qualify in minutes. Soft credit pull, offers from multiple lenders, terms up to 20 years.",
  cta: "Check Your Rate",
  link: "Learn more",
};

/** Promo banner — one line beneath the offer. */
export const PROMO_FINANCING_LINE = { prefix: "Financing available — ", link: "check your rate" };

// ---- Prompt 3: estimator callout, request-form checkbox, /financing sticky bar ----

/** Acorn Finance's minimum loan. The estimator callout shows only when the displayed high end is at least this. */
export const FINANCING_MIN_JOB = 1000;

/** Estimator result step — compact callout directly below the range (placement estimator-result). */
export const ESTIMATOR_FINANCING = {
  text: "Want to spread this out? Pre-qualify for financing in minutes — no impact to your credit score.",
  cta: "Check Your Rate",
  link: "Learn more",
};

/** Request Service form — optional checkbox + confirmation-state CTA (placement form-confirmation). */
export const FORM_FINANCING = {
  checkbox: "I'd like information about financing.",
  confirmation: "Want a head start? Pre-qualify now — it takes a few minutes and won't affect your credit score.",
  cta: "Check Your Rate",
};

// The /financing-only "Check Your Rate" sticky-bar variant (placement financing-sticky)
// was retired by SEO audit batch 2, Part 4: the phone bar now has the same three
// actions (Call / Text a photo / Estimate) on every page. Check Your Rate stays on
// the page itself (hero, mid-page band, footer CTA).

/**
 * High end of the range the estimator shows, in dollars — the number the
 * FINANCING_MIN_JOB threshold is compared against. `range` results use `high`
 * (or `low` for open-ended "$X+" ranges); `stacked` results use the largest
 * card; message / route results have no range, so `null` (no callout).
 */
export const rangeHighEnd = (result) => {
  if (!result) return null;
  const top = (r) => {
    const hi = Number(r?.high);
    const lo = Number(r?.low);
    if (Number.isFinite(hi) && hi > 0) return hi;
    return Number.isFinite(lo) && lo > 0 ? lo : null;
  };
  if (result.type === "range") return top(result);
  if (result.type === "stacked") {
    const tops = (result.cards || []).map(top).filter((n) => n !== null);
    return tops.length ? Math.max(...tops) : null;
  }
  return null;
};

export const qualifiesForFinancing = (result) => {
  const high = rangeHighEnd(result);
  return high !== null && high >= FINANCING_MIN_JOB;
};
